import { cardLabel, newDecks, rankOf, type Card } from '../cards';
import type { Rng } from '../rng';
import { drawWithLuck, luckOf, secondChanceFrame, secondChanceRate, type Outcome } from './luck';
import type { CasinoGame } from './types';

/**
 * Commission-free punto banco from an eight-deck shoe with the standard
 * third-card rules. Player pays 1:1 (~1.2% edge). Banker pays 1:1, except a
 * Banker win on a total of 6 pays half (~1.5%) — the usual no-commission
 * rule, chosen because a 5% commission can't be paid in whole dollars on
 * most bets. Tie pays 8:1 (~14%); Player/Banker bets push on a tie. Bets
 * are always even so the half payout is a whole number.
 */
const DECKS = 8;
const RESHUFFLE_BELOW = 52;

export type BaccaratSide = 'player' | 'banker' | 'tie';
const SIDES: BaccaratSide[] = ['banker', 'player', 'tie'];
const SIDE_NAMES: Record<BaccaratSide, string> = { player: 'Player', banker: 'Banker', tie: 'Tie' };

export const TIE_PAYS = 8;

/** Standard eight-deck punto banco odds (per coup), for the ★'s rate. */
const P_BANKER = 0.458597;
const P_PLAYER = 0.446247;
const P_TIE = 0.095156;
/** Banker wins with a total of 6 (pays 1:2 here). */
const P_BANKER_SIX = 0.053863;

/** A side's results (for the ★'s second-chance rate). */
export function baccaratOutcomes(side: BaccaratSide): Outcome[] {
  if (side === 'tie') return [[P_TIE, TIE_PAYS + 1], [1 - P_TIE, 0]];
  if (side === 'player') return [[P_PLAYER, 2], [P_TIE, 1], [P_BANKER, 0]];
  return [[P_BANKER - P_BANKER_SIX, 2], [P_BANKER_SIX, 1.5], [P_TIE, 1], [P_PLAYER, 0]];
}

export function baccaratValue(card: Card): number {
  const r = rankOf(card);
  return r >= 9 ? 0 : r + 1;
}

export function total(cards: Card[]): number {
  return cards.reduce((s, c) => s + baccaratValue(c), 0) % 10;
}

export interface Coup {
  player: Card[];
  banker: Card[];
}

/** Plays out one coup with the standard drawing rules. */
export function playCoup(draw: () => Card): Coup {
  const player = [draw()];
  const banker = [draw()];
  player.push(draw());
  banker.push(draw());
  const p = total(player);
  const b = total(banker);
  if (p >= 8 || b >= 8) return { player, banker };
  let playerThird: number | null = null;
  if (p <= 5) {
    const card = draw();
    player.push(card);
    playerThird = baccaratValue(card);
  }
  const bankerDraws =
    playerThird === null
      ? b <= 5
      : b <= 2 ||
        (b === 3 && playerThird !== 8) ||
        (b === 4 && playerThird >= 2 && playerThird <= 7) ||
        (b === 5 && playerThird >= 4 && playerThird <= 7) ||
        (b === 6 && (playerThird === 6 || playerThird === 7));
  if (bankerDraws) banker.push(draw());
  return { player, banker };
}

export function winner(coup: Coup): BaccaratSide {
  const p = total(coup.player);
  const b = total(coup.banker);
  return p > b ? 'player' : b > p ? 'banker' : 'tie';
}

/** Total returned (stake included) for a bet on `side`. */
export function baccaratReturn(side: BaccaratSide, coup: Coup, bet: number): number {
  const result = winner(coup);
  if (result === 'tie') return side === 'tie' ? bet * (TIE_PAYS + 1) : bet;
  if (side !== result) return 0;
  if (side === 'banker' && total(coup.banker) === 6) return bet + bet / 2;
  return bet * 2;
}

/** The coup's cards turning one at a time, in dealing order: Player,
 *  Banker, Player, Banker, then any third cards. */
export function coupFrames(coup: Coup): string[] {
  const steps: [number, number][] = [[1, 0], [1, 1], [2, 1], [2, 2]];
  if (coup.player.length === 3) steps.push([3, 2]);
  if (coup.banker.length === 3) steps.push([coup.player.length, 3]);
  const side = (name: string, cards: Card[], n: number) => `${name}: ${cards.slice(0, n).map(cardLabel).join(' ')}${n ? ` (${total(cards.slice(0, n))})` : ''}`;
  return steps.map(([p, b]) => [side('Player', coup.player, p), side('Banker', coup.banker, b)].join('\n'));
}

/** The jackpot coup: a tie between two two-card 9s. */
export function isJackpotCoup(coup: Coup): boolean {
  return coup.player.length === 2 && coup.banker.length === 2 && total(coup.player) === 9 && total(coup.banker) === 9;
}

export interface BaccaratState {
  side: BaccaratSide;
  shoe: Card[];
  last: Coup | null;
  lastBet: number;
  lastSide: BaccaratSide;
  returned: number;
  /** The ★'s luck re-dealt a losing coup. */
  lucky?: boolean;
}

function drawFrom(state: BaccaratState, rng: Rng): () => Card {
  return () => {
    if (state.shoe.length === 0) state.shoe = rng.shuffle(newDecks(DECKS));
    return state.shoe.pop()!;
  };
}

export const baccarat: CasinoGame<BaccaratState> = {
  id: 'baccarat',
  usesLuck: true,
  name: 'Baccarat',
  blurb: 'Bet on Player, Banker, or Tie. Closest to 9 wins.',
  baseMinBet: 4,
  baseMaxBet: 100,
  betStep: 2,
  specialPerk: 'Luck: a losing coup is sometimes dealt again.',
  jackpotRule: 'Win a Tie bet on two natural 9s.',
  create: () => ({ side: 'banker', shoe: [], last: null, lastBet: 0, lastSide: 'banker', returned: 0 }),
  locked: () => false,
  options: (state) => [
    { id: 'deal', label: 'Deal' },
    { id: 'side', label: `Bet on: ${SIDE_NAMES[state.side]}` },
  ],
  describe(state, env, highlighted) {
    if (highlighted === 'side') {
      const six = '1:2';
      return `Player pays 1:1.\nBanker pays 1:1 (${six} if it wins on 6).\nTie pays 8:1.\n\nTap to change.`;
    }
    const coup = state.last;
    if (!coup) return 'Tap Deal. Hands closest to 9 win; tens and faces count 0.';
    const result = winner(coup);
    const net = state.returned - state.lastBet;
    const outcome = net > 0 ? `${state.lucky ? '★ ' : ''}+$${net}` : net === 0 ? 'Push' : `-$${-net}`;
    return [
      `Player: ${coup.player.map(cardLabel).join(' ')} (${total(coup.player)})`,
      `Banker: ${coup.banker.map(cardLabel).join(' ')} (${total(coup.banker)})`,
      '',
      result === 'tie' ? 'Tie' : `${SIDE_NAMES[result]} wins`,
      `Your ${SIDE_NAMES[state.lastSide]} bet: ${outcome}`,
    ].join('\n');
  },
  act(state, optionId, env) {
    if (optionId === 'side') {
      return { state: { ...state, side: SIDES[(SIDES.indexOf(state.side) + 1) % SIDES.length]! }, bankroll: env.bankroll };
    }
    if (optionId !== 'deal') return { state, bankroll: env.bankroll };
    const next: BaccaratState = { ...state, shoe: [...state.shoe] };
    if (next.shoe.length < RESHUFFLE_BELOW) next.shoe = env.rng.shuffle(newDecks(DECKS));
    // The ★'s luck may deal a losing coup again (only the new coup is shown).
    const q = secondChanceRate(baccaratOutcomes(state.side), luckOf(env));
    const { outcome: coup, lucky, first } = drawWithLuck(env.rng, () => playCoup(drawFrom(next, env.rng)), (c) => baccaratReturn(state.side, c, 2) < 2, q);
    const returned = baccaratReturn(state.side, coup, env.bet);
    return {
      state: { ...next, last: coup, lastBet: env.bet, lastSide: state.side, returned, lucky },
      bankroll: env.bankroll - env.bet + returned,
      settled: { wagered: env.bet, returned },
      jackpot: state.side === 'tie' && isJackpotCoup(coup),
      // The cards turn one at a time; a ★ second chance shows the losing
      // coup first, then deals again.
      frames: first ? [...coupFrames(first), secondChanceFrame(coupFrames(first).at(-1)!), ...coupFrames(coup)] : coupFrames(coup),
    };
  },
};
